"use client";

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { createClient } from '@/auth/client';

export interface ConnectedAccount {
  id: string;
  user_id: string;
  provider: string;
  status?: string;
  connection_type?: string;
  connected_social_account_id?: string;
  username?: string;
  // other fields from your table can be added here
}

export function useConnectedAccounts() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const supabase = useMemo(() => createClient(), []);

  const fetchAccounts = useCallback(async () => {
    if (!user?.id) {
      setAccounts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const { data, error } = await supabase
      .from('connected_social_accounts')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching connected accounts:', error);
      setAccounts([]);
    } else {
      setAccounts(data || []);
    }
    setIsLoading(false);
  }, [supabase, user?.id]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const channel = supabase
      .channel(`connected-social-accounts:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'connected_social_accounts',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchAccounts();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchAccounts, supabase, user?.id]);

  return { accounts, isLoading, loading: isLoading, refresh: fetchAccounts };
}
